class StatsCounter extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.animated = false;
    this.scrollContainer = null;
    this.init();
  }

  init() {
    this.setupIntersectionObserver();
    this.setupScrollContainer();
  }
  
  setupScrollContainer() {
    // Enhanced touch scroll for mobile
    this.scrollContainer = this.querySelector('.row');
    
    if (!this.scrollContainer) return;
    
    let isDown = false;
    let startX;
    let scrollLeft;
    
    // Add touch momentum for smoother scrolling
    this.scrollContainer.addEventListener('touchstart', (e) => {
      isDown = true;
      startX = e.touches[0].pageX - this.scrollContainer.offsetLeft;
      scrollLeft = this.scrollContainer.scrollLeft;
    });
    
    this.scrollContainer.addEventListener('touchmove', (e) => {
      if (!isDown) return;
      const x = e.touches[0].pageX - this.scrollContainer.offsetLeft;
      const walk = (x - startX) * 1.5; // Scroll speed multiplier
      this.scrollContainer.scrollLeft = scrollLeft - walk;
    });
    
    this.scrollContainer.addEventListener('touchend', () => {
      isDown = false;
    });
    
    // Hide scroll indicator after first interaction
    this.scrollContainer.addEventListener('scroll', () => {
      const statsInner = this.querySelector('.stats__inner');
      if (statsInner && statsInner.classList) {
        statsInner.style.setProperty('--scroll-indicator-opacity', '0');
      }
    }, { once: true });
  }

  setupIntersectionObserver() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.animated) {
              this.animateNumbers();
              this.animated = true;
            }
          });
        },
        {
          threshold: 0.3,
          rootMargin: '0px 0px -50px 0px'
        }
      );
      this.observer.observe(this);
    } else {
      // Fallback for older browsers
      this.animateNumbers();
    }
  }

  animateNumbers() {
    const enableAnimation = this.dataset.enableAnimation === 'true';
    
    if (!enableAnimation) {
      return;
    }

    const numberElements = this.querySelectorAll('.stats__number');
    const animationDuration = parseFloat(this.style.getPropertyValue('--animation-duration')) || 2;

    numberElements.forEach((element, index) => {
      const targetValue = element.dataset.target;
      
      if (!targetValue || isNaN(parseInt(targetValue))) {
        return;
      }

      const finalValue = parseInt(targetValue);
      const originalText = element.textContent;
      
      // Extract suffix (like +, %, etc.) from original text
      const suffix = originalText.replace(/[\d,.\s]/g, '');
      
      // Add loading class
      element.classList.add('loading');
      
      // Delay each animation slightly for staggered effect
      setTimeout(() => {
        this.animateNumber(element, 0, finalValue, animationDuration, suffix);
      }, index * 200);
    });
  }

  animateNumber(element, start, end, duration, suffix = '') {
    const startTime = performance.now();
    const isDecimal = end.toString().includes('.');
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);
      
      // Easing function for smooth animation
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      
      const currentValue = Math.floor(start + (end - start) * easeOutCubic);
      
      // Format number with commas for thousands
      const formattedValue = this.formatNumber(currentValue, isDecimal);
      element.textContent = formattedValue + suffix;
      
      // Add pulse effect when number changes
      if (currentValue !== start) {
        element.classList.add('animated');
        setTimeout(() => element.classList.remove('animated'), 300);
      }
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation complete
        element.classList.remove('loading');
        element.classList.add('completed');
        
        // Ensure final value is correct
        const finalFormattedValue = this.formatNumber(end, isDecimal);
        element.textContent = finalFormattedValue + suffix;
      }
    };
    
    requestAnimationFrame(animate);
  }

  formatNumber(num, isDecimal = false) {
    if (isDecimal) {
      return num.toFixed(1);
    }
    return num.toLocaleString();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Register the custom element
if (!customElements.get('stats-counter')) {
  customElements.define('stats-counter', StatsCounter);
}

// Initialize any existing stats counters on page load
document.addEventListener('DOMContentLoaded', () => {
  const statsCounters = document.querySelectorAll('stats-counter');
  statsCounters.forEach(counter => {
    if (!customElements.get('stats-counter')) {
      customElements.define('stats-counter', StatsCounter);
    }
  });
});

// Re-initialize when new sections are loaded (for dynamic content)
document.addEventListener('shopify:section:load', (event) => {
  const statsCounter = event.target.querySelector('stats-counter');
  if (statsCounter) {
    // Reset animation state for new sections
    statsCounter.animated = false;
    if (statsCounter.observer) {
      statsCounter.observer.disconnect();
    }
    statsCounter.setupIntersectionObserver();
  }
});
